import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChannelList,
  InfiniteScroll,
  InfiniteScrollProps,
  useChatContext,
  CustomQueryChannelParams,
  DefaultStreamChatGenerics,
} from "stream-chat-react";
import { Channel, Event } from "stream-chat";
import { MessagingChannelListHeader, MessagingChannelPreview } from "../index";
import { useThemeContext } from "../../context";
import { ChatType } from "../../enums/ChatType";
import ChatTabs from "../ChatTab/ChatTabs";

type TabKey = "dm" | "event";

/**
 * Props for the MessagingSidebar component
 */

interface MessagingSidebarProps {
  channelListOptions: any;
  onClick: () => void;
  onCreateChannel: () => void;
  activeChannel: Channel<DefaultStreamChatGenerics> | null | undefined;
  channelQueryParams: { eventId: number | string };
  platformType: string;
  languageCode: string;
  eventName: string;
  groupIds: number[];
  openDmTab: boolean; // Flag to open DM tab
  dmTabName: string;
  eventChatName: string;
  onTabChange?: (tab: ChatType) => void;
}

const MessagingSidebar: React.FC<MessagingSidebarProps> = ({
  channelListOptions,
  onClick,
  onCreateChannel,
  activeChannel,
  channelQueryParams,
  platformType,
  languageCode,
  eventName,
  groupIds: userGroupIds,
  openDmTab,
  dmTabName,
  eventChatName,
  onTabChange
}) => {
  const { themeClassName } = useThemeContext();
  const { client, setActiveChannel, channel: currentActiveChannel } = useChatContext();

  const [activeTab, setActiveTab] = useState<ChatType.DM | ChatType.EventChat>(
    openDmTab ? ChatType.DM : ChatType.EventChat
  );

  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  // Refs for measuring container and tabs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState<number>(0);

  const recomputeListHeight = useCallback(() => {
    let headerHeight = 0;
    if (client.user?.role === "moderator" || platformType === "Mobile") {
      const headerElement = document.querySelector('.messaging__channel-list__header');
      headerHeight = headerElement ? headerElement.clientHeight : 60;
    }
    const containerH = containerRef.current?.clientHeight ?? 0;
    const tabsH = tabsRef.current?.clientHeight ?? 0;
    const next = Math.max(0, containerH - tabsH - headerHeight);
    setListHeight(next);
  }, []);

  // Recompute height on mount, resize, and when elements change size
  useLayoutEffect(() => {
    recomputeListHeight();

    const onResize = () => recomputeListHeight();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [recomputeListHeight]);

  const cachesRef = useRef({
    dm: { fetched: new Set<string>(), offset: 0, lastQuery: 0 },
    event: { fetched: new Set<string>(), offset: 0, lastQuery: 0 },
  });

  // Ref to store setChannels functions for both tabs so we can update channels on events
  const channelSettersRef = useRef<{
    dm?: Dispatch<SetStateAction<Channel<DefaultStreamChatGenerics>[]>>;
    event?: Dispatch<SetStateAction<Channel<DefaultStreamChatGenerics>[]>>;
  }>({});

  // Ref to remember the last known ordering of channel IDs per tab.
  // Helps us reinsert channels at the same index if they temporarily disappear.
  const channelOrderRef = useRef<{ dm: string[]; event: string[] }>({ dm: [], event: [] });

  // Records the order of channel IDs for a given tab
  const recordChannelOrder = (tabKey: TabKey, channels: Channel<DefaultStreamChatGenerics>[]) => {
    channelOrderRef.current[tabKey] = channels
      .map((channel) => channel.id)
      .filter((id): id is string => typeof id === "string");
  };

  // Gets the last known index of a channel ID in the recorded order for a tab
  const getLastKnownIndex = (tabKey: TabKey, channelId?: string | null) => {
    if (!channelId) return -1;
    const order = channelOrderRef.current[tabKey] ?? [];
    return order.indexOf(channelId);
  };

  // Inserts a channel at its last known position in the list, or at the end if unknown
  const insertAtKnownPosition = (
    tabKey: TabKey,
    prev: Channel<DefaultStreamChatGenerics>[],
    channel: Channel<DefaultStreamChatGenerics>
  ) => {
    const lastKnownIndex = getLastKnownIndex(tabKey, channel.id);
    if (lastKnownIndex < 0) {
      return [...prev, channel];
    }

    const insertAt = Math.min(lastKnownIndex, prev.length);
    const updated = [...prev];
    updated.splice(insertAt, 0, channel);
    return updated;
  };

  // Converts ChatType to TabKey string
  const getTabKey = (t: ChatType): TabKey => (t === ChatType.DM ? "dm" : "event");

  // Creates a setter that records channel order whenever channels are set
  const createOrderedSetter = (
    tabKey: TabKey,
    setter: Dispatch<SetStateAction<Channel<DefaultStreamChatGenerics>[]>>
  ) => {
    return (value: SetStateAction<Channel<DefaultStreamChatGenerics>[]>) => {
      if (typeof value === "function") {
        setter((prev) => {
          const next = (value as (prevChannels: Channel<DefaultStreamChatGenerics>[]) => Channel<DefaultStreamChatGenerics>[])(prev);
          recordChannelOrder(tabKey, next);
          return next;
        });
      } else {
        recordChannelOrder(tabKey, value);
        setter(value);
      }
    };
  };

  // Updates the channel list for a specific tab based on visibility
  const updateChannelListForTab = useCallback(
    (tabKey: TabKey, channel: Channel<DefaultStreamChatGenerics>, shouldBeVisible: boolean) => {
      const setChannels = channelSettersRef.current[tabKey];
      if (!setChannels) return;

      setChannels((prev) => {
        const index = prev.findIndex((c) => c.id === channel.id);

        if (shouldBeVisible) {
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = channel;
            return updated;
          }

          return insertAtKnownPosition(tabKey, prev, channel);
        }

        if (index >= 0) {
          return prev.filter((c) => c.id !== channel.id);
        }

        return prev;
      });
    },
    []
  );

  const pageLimit = client.user?.role === "moderator" ? 30 : 15;

  const openedChannelRef = useRef<string | null>(null);

  // Effect to handle automatic opening of activeChannel
  useEffect(() => {
    if (activeChannel !== undefined && activeChannel !== null && activeChannel.id) {
      setActiveChannel(activeChannel);
      
      // Only automatically open the channel if:
      // 1. It's a different channel than what we've already opened
      // 2. We haven't opened any channel yet
      // 3. The current active channel is different
      const shouldOpenChannel = (
        openedChannelRef.current !== activeChannel.id &&
        (!currentActiveChannel || currentActiveChannel.id !== activeChannel.id)
      );
      
      if (shouldOpenChannel) {
        openedChannelRef.current = activeChannel.id;
        onClick();
      }
    }
  }, [activeChannel, onClick, setActiveChannel, currentActiveChannel]);

  // Reset the opened channel ref when the current active channel changes
  // This allows the back button to work properly
  useEffect(() => {
    if (currentActiveChannel?.id) {
      openedChannelRef.current = currentActiveChannel.id;
    } else {
      // If no active channel, reset the ref to allow opening channels again
      openedChannelRef.current = null;
    }
  }, [currentActiveChannel]);

  /**
 * FUNCTION: buildFilters
 * 
 * Builds Stream Chat query filters based on user role and active tab
 * Returns array of filter objects for OR query logic
 * 
 * MODERATOR:
 * - Returns all public channel types (with/without membership, watchlive)
 * - Moderators see all event channels regardless of tab
 * 
 * DM TAB (ChatType.DM):
 * - Returns messaging channels where user is member
 * - Includes both hidden and visible channels
 * 
 * EVENTCHAT TAB:
 * - Returns public_with_membership (group channels)
 * - Returns public_without_membership_watchlive (watch live channels)
 * 
 * PARAMETERS:
 * - forTab: Which tab to build filters for (DM or EventChat)
 * 
 * RETURNS:
 * Array of filter objects passed to Stream Chat queryChannels()
 */
  const buildFilters = useCallback(
    (forTab: ChatType) => {
      // MODERATOR: Get all public channels without filtering
      if (client.user?.role === "moderator") {
        return [
          { type: "public_without_membership", eventId: channelQueryParams.eventId },
          { type: "public_with_membership", eventId: channelQueryParams.eventId },
          { type: "public_without_membership_watchlive", eventId: channelQueryParams.eventId },
        ];
      }

      // DM TAB: Get messaging channels where user is a member
      if (forTab === ChatType.DM) {
        const me = client.user?.id!;
        return [
          { type: "messaging", members: { $in: [me] }, eventId: channelQueryParams.eventId, hidden: false },
          { type: "messaging", members: { $in: [me] }, eventId: channelQueryParams.eventId, hidden: true },
        ];
      }
      
      // EVENTCHAT TAB: Get public channels relevant to the event
      return [
        { type: "public_with_membership", eventId: channelQueryParams.eventId },
        { type: "public_without_membership_watchlive", eventId: channelQueryParams.eventId },
      ];
    },
    [client.user?.id, client.user?.role, channelQueryParams.eventId]
  );

   /**
   * Creates channel query functions for different tabs
   * that fetches and filters channels from Stream Chat.
   * It's called by ChannelList component to load channels for DM or EventChat tabs.
   * @param forTab - Which tab to query for (DM or EventChat)
   * @returns Async function that fetches and processes channels
   */
  const makeCustomQueryForTab = useCallback((forTab: ChatType) => async ({
    setChannels,
  }: CustomQueryChannelParams & { offset?: number }) => {
        
    // Get tab key ("dm" or "event") and create ordered setter to maintain channel order
    const tab = getTabKey(forTab);
    const setChannelsWithOrder = createOrderedSetter(tab, setChannels);

    // Store setter in ref so other functions (like customOnMessageNew) can access it
    channelSettersRef.current[tab] = setChannelsWithOrder;
    
    // Get cache for this tab (tracks fetched channels and offset for pagination)
    const cache = cachesRef.current[tab];
    
    // Throttle queries to prevent excessive API calls (max 1 query per 800ms)
    const now = Date.now();
    if (now - cache.lastQuery < 800) return;
    cache.lastQuery = now;
    
    // Current pagination offset (starts at 0, increments with each query)
    const offset = cache.offset;
    
    // Build filters based on tab type (messaging vs public channels)
    const filters = buildFilters(forTab);
    
    // Query Stream Chat for channels matching our filters
    // - $or: filters - Allows multiple filter conditions (e.g., different channel types)
    // - state: true - Include channel state (messages, members, etc.)
    // - presence: true - Include user presence information
    // - watch: true - Subscribe to real-time updates
    // - limit: pageLimit - Number of channels per page (15 for users, 30 for moderators)
    // - offset: Current pagination position
    const page = await client.queryChannels(
      { $or: filters },
      channelListOptions.sort,
      { state: true, presence: true, watch: true, limit: pageLimit, offset }
    );
    
    // Remove channels we've already fetched to avoid duplicates
    // Cache uses "type:id" format as unique keys
    const fresh = page.filter((ch) => {
      const key = `${ch.type}:${ch.id}`;
      if (cache.fetched.has(key)) return false; // Already have this channel
      cache.fetched.add(key); // Mark as fetched for future queries
      return true;
    });

    // Update offset for next pagination request
    cache.offset = offset + page.length;
    const currentUserId = client.user?.id;

    // Auto-load more channels if:
    // 1. We got a full page of results (might be more available)
    // 2. We haven't loaded too much yet (limit to 150 total)
    const shouldCheckForMoreData = fresh.length === pageLimit && cache.offset < 150;
    
    if (shouldCheckForMoreData && fresh.length > 0) {
      // Only auto-load in the first 3 batches (45 channels)
      // This prevents infinite loading while ensuring we have enough channels
      if (cache.offset <= 45) { // 45 = 3 batches of 15 channels each
        setTimeout(() => {
          cache.lastQuery = 0; // Reset throttle to allow immediate query
          makeCustomQueryForTab(forTab)({ 
            setChannels,
            currentChannels: [],
            queryType: 'reload' as const,
            setHasNextPage: () => {}
          });
        }, 100); // Small delay to avoid overwhelming the API
      }
    }

    
    // Public channels with group-based permissions
    // These channels may be restricted to specific participant groups
    const publicChannels = fresh.filter((channel) => {
      // Parse channel details which contain participantGroupIds
      const rawDetails = channel.data?.details as unknown;
      const details =
        typeof rawDetails === "string"
          ? (() => {
              try {
                return JSON.parse(rawDetails) as { participantGroupIds?: number[] | null };
              } catch {
                return null; // Invalid JSON - treat as no restrictions
              }
            })()
          : null;

      // Get the list of group IDs that can access this channel
      const channelGroupIds = details?.participantGroupIds ?? null;
      
      // Channel is open to all if no group restrictions are set
      const isOpenToAll =
        channelGroupIds === null ||
        (Array.isArray(channelGroupIds) && channelGroupIds.length === 0);

      if (isOpenToAll) return true;

      // Check if user's groups intersect with channel's allowed groups
      const intersectsUserGroups =
        Array.isArray(channelGroupIds) &&
        Array.isArray(userGroupIds) &&
        channelGroupIds.some((gid) => userGroupIds.includes(gid));      

      return (isOpenToAll || intersectsUserGroups);
    });

    // Watch live channels (special public channels for live events)
    const watchliveChannels = fresh.filter(
      (channel) => channel.type === "public_without_membership_watchlive"
    );

    // ROLE-BASED CHANNEL SELECTION    
    if (client.user?.role === "moderator") {
      // MODERATORS: Get ALL public channels without filtering
      // Moderators can see and manage all public channels regardless of restrictions
      const moderatorChannels = fresh.filter((channel) =>
        channel.type === "public_with_membership" || 
        channel.type === "public_without_membership" ||
        channel.type === "public_without_membership_watchlive"
      );

      // Add only new channels (avoid duplicates)
      setChannelsWithOrder((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const toAdd = moderatorChannels.filter((c) => !existingIds.has(c.id));
        return [...prev, ...toAdd];
      });
      
    } else {      
      //Blocked messaging channels (always shown regardless of messages)
      const blockedChannels = fresh.filter((c) => c.type === "messaging" && c.data?.blocked === true);
      
      // Unblocked messaging channels (must have messages to appear)
      // This prevents empty DM channels from cluttering the list
      const unblockedChannels = fresh.filter(
        (c) => {
         return(
          c.type === "messaging" &&
          c.data?.blocked === false 
          && (c.state?.messages?.length ?? 0) > 0 // Must have at least one message
       ) }
      );
      
      // Combine all channel types together
      const merged = [
        ...unblockedChannels,
        ...blockedChannels,
        ...publicChannels,
        ...watchliveChannels,
      ];

      // Remove duplicates (in case a channel appears in multiple categories)
      const mergedUnique = merged.filter(
        (ch, index, self) => index === self.findIndex((c) => c.id === ch.id)
      );
      
      // Sort by last_message_at timestamp (most recent messages first)
      // Channels without messages go to the end
      const sorted = mergedUnique.sort((a, b) => {
        const aAt = a.data?.last_message_at as string | undefined;
        const bAt = b.data?.last_message_at as string | undefined;
        
        // Both have messages - sort by timestamp (newest first)
        if (aAt && bAt) return bAt.localeCompare(aAt);
        
        // Only one has messages - put it first
        if (aAt && !bAt) return -1;
        if (!aAt && bAt) return 1;
        
        // Neither has messages - maintain original order (both go to end)
        return 0;
      });
      
      // Add only channels that aren't already in the list
      setChannelsWithOrder((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const toAdd = sorted.filter((c) => !seen.has(c.id));
        return [...prev, ...toAdd];
      });
    }
  });
  
  // Query function for DM (messaging) channels
  // Fetches channels where type = "messaging" and user is a member
  const queryDM = useMemo(() => makeCustomQueryForTab(ChatType.DM), [makeCustomQueryForTab]);
  
  // Query function for EventChat (public) channels  
  // Fetches channels where type = "public_with_membership" or "public_without_membership_watchlive"
  const queryEvent = useMemo(() => makeCustomQueryForTab(ChatType.EventChat), [makeCustomQueryForTab]);
  
  const Paginator = useCallback((props: InfiniteScrollProps) => (
    <InfiniteScroll {...props} threshold={100} />
  ), []);

  const Preview = useCallback((props: any) => (
    <MessagingChannelPreview
      {...props}
      onClick={onClick}
      setActiveChannel={setActiveChannel}
      languageCode={languageCode}
    />
  ), [setActiveChannel, languageCode]);

  // Helper function to check if a channel should be visible based on filters
  const shouldChannelBeVisible = useCallback(
    (channel: Channel<DefaultStreamChatGenerics>) => {
      // Check eventId match
      if (String(channel?.data?.eventId) !== String(channelQueryParams.eventId)) {
        return false;
      }

      // For moderators, show all public channels
      if (client.user?.role === "moderator") {
        return (
          channel.type === "public_with_membership" ||
          channel.type === "public_without_membership" ||
          channel.type === "public_without_membership_watchlive"
        );
      }

      // For messaging channels (DM), check if user is a member and has messages (for unblocked channels)
      if (channel.type === "messaging") {
        if (channel.data?.blocked === false) {
          // Check last_message_at first (most reliable indicator of messages)
          // This matches the logic in customChannelFilterFunction to ensure consistency
          const hasLastMessage = channel?.data?.last_message_at != null || channel?.state?.last_message_at != null;
          // Fallback: check messages length if last_message_at is not available
          const hasMessagesInState = (channel?.state?.messages?.length ?? 0) > 0;
          // Channel has messages if either condition is true
          return hasLastMessage || hasMessagesInState;
        }
        // Blocked channels can be shown even without messages
        return true;
      }

      // For public channels, check group membership
      if (
        channel.type === "public_with_membership" ||
        channel.type === "public_without_membership" ||
        channel.type === "public_without_membership_watchlive"
      ) {
        const rawDetails = channel.data?.details as unknown;
        const details =
          typeof rawDetails === "string"
            ? (() => {
                try {
                  return JSON.parse(rawDetails) as { participantGroupIds?: number[] | null };
                } catch {
                  return null;
                }
              })()
            : null;

        const channelGroupIds = details?.participantGroupIds ?? null;
        const isOpenToAll =
          channelGroupIds === null ||
          (Array.isArray(channelGroupIds) && channelGroupIds.length === 0);

        if (isOpenToAll) return true;

        const intersectsUserGroups =
          Array.isArray(channelGroupIds) &&
          Array.isArray(userGroupIds) &&
          channelGroupIds.some((gid) => userGroupIds.includes(gid));

        return isOpenToAll || intersectsUserGroups;
      }

      return false;
    },
    [channelQueryParams.eventId, client.user?.role, userGroupIds]
  );

  // Handler for when a new message is received in a channel
  const customOnMessageNew = useCallback((
    setChannels: Dispatch<SetStateAction<Channel<DefaultStreamChatGenerics>[]>>,
    event: Event<DefaultStreamChatGenerics>
  ) => {
    const ch: any = event.channel;
    if (!ch) return;

    if (String(ch?.data?.eventId) !== String(channelQueryParams.eventId)) return;

    const belongsToTab = ch.type === "messaging" ? ChatType.DM : ChatType.EventChat;
    if (belongsToTab !== activeTab) return;

    /**
     * STEP 1: Watch channel and fetch complete data
     * Creates a channel reference and watches it to fetch complete channel data.
     * This is necessary because the event might not contain all channel properties
     * (like eventId, group membership, message count, etc.) that we need for filtering.
     */
    (async () => {
      try {
        const newChannel = client.channel(ch.type, ch.id);
        // Watch the channel to load its state and data
        // This ensures the channel has all necessary properties including last_message_at
        await newChannel.watch();
        console.log("Watch called on channel:", newChannel.id);

        /**
         * STEP 2: Check if channel should be visible
         * Use shouldChannelBeVisible to ensure consistency with customChannelFilterFunction.
         * This ensures that channels added via customOnMessageNew will pass the filter check.
         */
        const shouldBeVisible = shouldChannelBeVisible(newChannel);

        /**
         * STEP 3: Get the correct channel setter for this tab
         * Each tab (DM and EventChat) has its own channel list state.
         * We get the appropriate setter function from the ref.
         */
        const tabKey = getTabKey(belongsToTab);
        const targetSetter = channelSettersRef.current[tabKey] ?? setChannels;

        /**
         * STEP 4: Update channel list
         * If channel is already in the list, move it to the top (most recent message first).
         * If channel is not in the list and should be visible, add it to the top.
         * Since we're handling a new message event, if the channel is not in the list,
         * it means it's receiving its first message, so we should add it.
         */
        targetSetter((prev) => {
          const index = prev.findIndex((c) => c.id === newChannel.id);
          if (index >= 0) {
            // Channel already exists - move it to the top (most recent message first)
            const updated = [...prev];
            const [existing] = updated.splice(index, 1);
            return [existing, ...updated];
          }
          // Channel doesn't exist in list - this is a new message event, so add it if visible
          // Since we're in message.new event, we know there's a message, so add the channel
          if (shouldBeVisible) {
            return [newChannel, ...prev];
          }
          // Channel should not be visible - don't add it
          return prev;
        });
      } catch (error) {
        /**
         * STEP 5: Error handling
         * If any step fails (network error, invalid data, etc.),
         * log the error to console but don't crash the application.
         */
        console.error("Error handling new message:", error);
      }
    })();
  }, [activeTab, channelQueryParams.eventId, client, shouldChannelBeVisible]);

  // Listen to client-wide message.new events for channels not in the list
  // This is needed because ChannelList's onMessageNew only fires for channels already in the list
  useEffect(() => {
    /**
     * STEP 1: handleMessageNew - Main event handler function for client-wide message.new events
     * This function is called automatically when Stream Chat detects a new message event
     * for ANY channel, including channels not currently in the list.
     */
    const handleMessageNew = async (event: Event<DefaultStreamChatGenerics>) => {
      /**
       * STEP 2: Extract channel information from event
       * message.new events have channel info in different formats:
       * - event.cid: "type:id" format (e.g., "messaging:user123")
       * - event.channel_type and event.channel_id: separate properties
       * - event.channel: full channel object (if available)
       */
      const eventAny: any = event;
      let channelType: string | undefined;
      let channelId: string | undefined;
      
      // Try to get channel info from event.cid (most common format)
      if (eventAny.cid) {
        const parts = eventAny.cid.split(':');
        if (parts.length >= 2) {
          channelType = parts[0];
          channelId = parts.slice(1).join(':'); // Handle IDs that might contain colons
        }
      }
      
      // Fallback to separate channel_type and channel_id properties
      if (!channelType && eventAny.channel_type) {
        channelType = eventAny.channel_type;
        channelId = eventAny.channel_id;
      }
      
      // Fallback to event.channel if available
      if (!channelType && eventAny.channel) {
        const ch = eventAny.channel;
        if (ch.cid) {
          const parts = ch.cid.split(':');
          if (parts.length >= 2) {
            channelType = parts[0];
            channelId = parts.slice(1).join(':');
          }
        } else if (ch.type && ch.id) {
          channelType = ch.type;
          channelId = ch.id;
        }
      }
      
      // If we still don't have channel info, exit
      if (!channelType || !channelId) {
        return;
      }

      /**
       * STEP 4: Determine which tab this channel belongs to
       * Channels are organized into two tabs:
       * - DM tab: for "messaging" type channels (direct messages)
       * - EventChat tab: for all other channel types (public channels)
       */
      const belongsToTab = channelType === "messaging" ? ChatType.DM : ChatType.EventChat;
      
      /**
       * STEP 5: Check if channel belongs to active tab
       * Only process channels that belong to the currently active tab.
       */
      if (belongsToTab !== activeTab) {
        return;
      }

      /**
       * STEP 6: Get the channel setter for this tab
       * Each tab (DM and EventChat) has its own channel list state.
       * We get the appropriate setter function from the ref.
       */
      const tabKey = getTabKey(belongsToTab);
      const targetSetter = channelSettersRef.current[tabKey];
      
      // If we don't have a setter yet, the ChannelList hasn't initialized, skip for now
      if (!targetSetter) {
        return;
      }

      /**
       * STEP 7: Watch channel and check visibility
       * Creates a channel reference and watches it to fetch complete channel data.
       * This is necessary because the event might not contain all channel properties
       * (like eventId) that we need for filtering.
       */
      try {
        const newChannel = client.channel(channelType, channelId);
        
        // Watch the channel to establish connection
        await newChannel.watch();
        
        // Query the channel state to ensure we have the latest data including last_message_at
        // This is important because watch() might not immediately populate all state
        await newChannel.query({ 
          messages: { limit: 1 },
          state: true 
        });
        
        // If the event has message data and last_message_at is not set, use the message timestamp
        if (event.message && !newChannel.data?.last_message_at && !newChannel.state?.last_message_at) {
          const messageTime = event.message.created_at || event.message.updated_at;
          if (messageTime) {
            newChannel.data = { ...newChannel.data, last_message_at: messageTime };
          }
        }

        /**
         * STEP 8: Verify channel belongs to current event
         * Checks if the channel is part of the current event.
         * If it belongs to a different event, we ignore it and exit.
         */
        if (String(newChannel?.data?.eventId) !== String(channelQueryParams.eventId)) {
          return;
        }

        /**
         * STEP 9: Check if channel should be visible
         * Use shouldChannelBeVisible to ensure consistency with customChannelFilterFunction.
         */
        const shouldBeVisible = shouldChannelBeVisible(newChannel);

        if (shouldBeVisible) {
          /**
           * STEP 10: Add channel to list if it's not already there
           * The setter will handle checking for duplicates, but we'll check here too
           * to avoid unnecessary work.
           */
          targetSetter((prev) => {
            const index = prev.findIndex((c) => c.id === newChannel.id);
            if (index >= 0) {
              // Channel already exists - move it to the top (most recent message first)
              const updated = [...prev];
              const [existing] = updated.splice(index, 1);
              return [existing, ...updated];
            }
            // Channel doesn't exist - add it to the top
            return [newChannel, ...prev];
          });
        }
      } catch (error) {
        /**
         * STEP 11: Error handling
         * If any step fails (network error, invalid data, etc.),
         * log the error to console but don't crash the application.
         */
        console.error("Error handling client-wide message.new event:", error);
      }
    };

    // Listen to message.new events on the client
    client.on('message.new', handleMessageNew);

    // Cleanup listener on unmount
    return () => {
      client.off('message.new', handleMessageNew);
    };
  }, [activeTab, channelQueryParams.eventId, client, shouldChannelBeVisible]);

  // Listen to client-wide channel.updated events and update the channel list accordingly
  useEffect(() => {
    /**
     * STEP 1: handleChannelUpdated - Main event handler function
     * This function is called automatically when Stream Chat detects a channel update event.
     * It receives an event object containing information about the updated channel.
     */
    const handleChannelUpdated = async (event: Event<DefaultStreamChatGenerics>) => {
      /**
       * STEP 2: Extract channel from event
       * Gets the channel object from the event. If no channel exists, exit early.
       */
      const ch: any = event.channel;
      if (!ch) return;

      try {
        /**
         * STEP 3: Extract channel type and ID
         * Channels can have their identifier in two formats:
         * - cid format: "type:id" (e.g., "messaging:user123")
         * - separate properties: ch.type and ch.id
         * We need to handle both cases to get the channel type and ID.
         */
        let channelType: string;
        let channelId: string;
        
        if (ch.cid) {
          // Channel ID is in format "type:id" - split it to get both parts
          const parts = ch.cid.split(':');
          channelType = parts[0];  // First part is the type (e.g., "messaging")
          channelId = parts[1];    // Second part is the ID (e.g., "user123")
        } else if (ch.type && ch.id) {
          // Channel has separate type and id properties - use them directly
          channelType = ch.type;
          channelId = ch.id;
        } else {
          // No valid channel identifier found - exit early
          return;
        }

        /**
         * STEP 4: Get full channel data by watching it
         * Creates a channel reference and watches it to fetch complete channel data.
         * This is necessary because the event might not contain all channel properties
         * (like eventId) that we need for filtering.
         */
        const updatedChannel = client.channel(channelType, channelId);
        console.log("Client.channel called for updated channel:", updatedChannel.id);
        /**
         * STEP 5: Verify channel belongs to current event
         * Checks if the updated channel is part of the current event.
         * If it belongs to a different event, we ignore it and exit.
         */
        if (String(updatedChannel.data?.eventId) !== String(channelQueryParams.eventId)) {
          return;
        }

        /**
         * STEP 6: Determine which tab this channel belongs to
         * Channels are organized into two tabs:
         * - DM tab: for "messaging" type channels (direct messages)
         * - EventChat tab: for all other channel types (public channels)
         * We need to know which tab to update.
         */
        const belongsToTab = updatedChannel.type === "messaging" ? ChatType.DM : ChatType.EventChat;
        const tab = getTabKey(belongsToTab); // Converts ChatType to "dm" or "event" string
        if (belongsToTab !== activeTab) {
          return;
        }

        /**
         * STEP 8: Check if channel should be visible
         * Calls shouldChannelBeVisible helper function to determine if the channel
         * should appear in the list based on:
         * - Event ID match
         * - User role (moderator vs regular user)
         * - Channel type and permissions
         * - Group membership (for public channels)
         * - Message count (for DM channels)
         */
        const shouldBeVisible = shouldChannelBeVisible(updatedChannel);

        updateChannelListForTab(tab, updatedChannel, shouldBeVisible);
      } catch (error) {
        /**
         * STEP 10: Error handling
         * If any step fails (network error, invalid data, etc.),
         * log the error to console but don't crash the application.
         */
        console.error("Error handling channel update:", error);
      }
    };

    // Listen to channel.updated events on the client
    client.on('channel.updated', handleChannelUpdated);

    // Cleanup listener on unmount
      return () => {
      client.off('channel.updated', handleChannelUpdated);
    };
  }, [activeTab, channelQueryParams.eventId, client, shouldChannelBeVisible, updateChannelListForTab]);

  
  /**
   * Final filter applied to channels before display
   * This function is called by ChannelList component AFTER channels are fetched and added to the list.
   * This runs on EVERY render when the channel list updates, so it must be efficient.
   * 
   * @param channels - Array of channels to filter
   * @param requiredType - Expected channel type ("messaging" for DM tab, "public_with_membership" for EventChat tab)
   * @param userGroupIds - Array of group IDs the user belongs to (for group-based permission checking)
   * @returns Filtered array of channels that should be displayed
   */
  const customChannelFilterFunction = (
  channels: Channel[],
  requiredType: string,
  userGroupIds: number[] = []
) => {
  return channels.filter((ch) => {

    // Verify channel belongs to the current event, channels from other events should NEVER appear.
    const belongsToThisEvent = (ch.data?.eventId === channelQueryParams.eventId);
    if(!belongsToThisEvent) return false;
  
    // Moderators see ALL public channels regardless of restrictions
    if(client.user?.role === "moderator")
    {
      return (ch.type === "public_with_membership" ||
              ch.type === "public_without_membership" ||
              ch.type === "public_without_membership_watchlive") &&
             (ch.data?.eventId === channelQueryParams.eventId);
    }
    
    /**
     * Channel type must match the required type for this tab
     * - DM tab expects "messaging" type
     * - EventChat tab expects "public_with_membership" type
     * This prevents messaging channels from appearing in EventChat tab and vice versa.
     */
    if (ch.type !== requiredType) return false;
    
    /**
     * For messaging (DM) channels, verify user is a member
     * Users should only see DM conversations they're part of.
     * This check happens BEFORE the message count check because:
     * - Non-members should never see the channel, even if it has messages
     * - It's more efficient to check membership first
     */
    if (ch.type === "messaging") {
      const isMember = !!ch.state?.members?.[client.user?.id || ''];
      if (!isMember) return false;
    }
    
    /**
     * For public_with_membership channels, check group-based access control
     *
     * Access rules:
     * 1. If participantGroupIds is null or empty → Channel is open to ALL users
     * 2. If participantGroupIds has values → User must be in at least ONE of those groups
     * 
     */
    if(ch.type === "public_with_membership") {
      // Parse the details field (stored as JSON string in channel data)
      const rawDetails = ch.data?.details as unknown;
      const details =
        typeof rawDetails === "string"
          ? (() => {
              try {
                return JSON.parse(rawDetails) as { participantGroupIds?: number[] | null };
              } catch {
                // If JSON parsing fails, treat as no restrictions (open to all)
                return null;
              }
            })()
          : null;
          
      // Extract the list of group IDs that can access this channel
      const channelGroupIds = details?.participantGroupIds ?? null;

      // Channel is open to all if no group restrictions are set
      // null = no restrictions, empty array = no restrictions
      const isOpenToAll =
        channelGroupIds === null ||
        (Array.isArray(channelGroupIds) && channelGroupIds.length === 0);

      if (isOpenToAll) return true;

      // Channel is restricted - check if user's groups intersect with channel's allowed groups
      // User needs to be in AT LEAST ONE group that the channel allows
      const intersectsUserGroups =
        Array.isArray(channelGroupIds) &&
        Array.isArray(userGroupIds) &&
        channelGroupIds.some((gid) => userGroupIds.includes(gid));

      return intersectsUserGroups;
    }
    else 
    {
      /**
       * For unblocked channels, we only show them if they have at least one message.
       * This prevents empty DM channels from cluttering the list.
      */
      if (ch.data?.blocked === false)
      {
          // Primary check: last_message_at timestamp (most reliable)
          // Check both data.last_message_at and state.last_message_at for robustness
          const hasLastMessage = ch?.data?.last_message_at != null || ch?.state?.last_message_at != null;
          
          // Fallback check: messages array length
          // Used when last_message_at might not be set yet
          const hasMessagesInState = (ch?.state?.messages?.length ?? 0) > 0;
          
          // Channel is visible if EITHER condition is true
          const hasMessages = hasLastMessage || hasMessagesInState;
          return hasMessages;
      }
      /**
       * Blocked channels are ALWAYS shown regardless of message count.
       * Without this, users might forget who they've blocked or lose access to channel history.
       */
      return true;
    }
  });
};


  return (
    <div ref={containerRef} className={`str-chat messaging__sidebar str-messaging-sidebar ${themeClassName} show`} id="mobile-channel-list">
      {(platformType !== "BlazorPWA" || client.user?.role === "moderator") && (
        <MessagingChannelListHeader
          onCreateChannel={onCreateChannel}
          platformType={platformType}
          languageCode={languageCode}
          eventName={eventName}
        />
      )}

      {client.user?.role !== "moderator" && (
        <div ref={tabsRef}>
          <ChatTabs
          languageCode={languageCode}
            activeTab={activeTab}
            onTabChange={(tab: ChatType) => setActiveTab(tab)}
            dmTabName={dmTabName}
            eventChatName={eventChatName}
          />
       </div>
      )}
      
      <div className="str-channel-list" style={{ display: activeTab === ChatType.DM ? "block" : "none",height: listHeight ? `${listHeight}px` : undefined }}>
        <ChannelList
          setActiveChannelOnMount={false}
          customQueryChannels={queryDM}
          onMessageNew={customOnMessageNew}
          allowNewMessagesFromUnfilteredChannels={true}
          channelRenderFilterFn={(channels) =>
            customChannelFilterFunction(channels, "messaging") // DM type
          }
          Preview={Preview}
          Paginator={Paginator}
          onClick={onClick} setActiveChannel={setActiveChannel} languageCode={languageCode} 
        />
      </div>

      <div className="str-channel-list" style={{ display: activeTab === ChatType.EventChat ? "block" : "none",height: listHeight ? `${listHeight}px` : undefined }}>
        <ChannelList
          setActiveChannelOnMount={false}
          customQueryChannels={queryEvent}
          onMessageNew={customOnMessageNew}
          sendChannelsToList={true}
          allowNewMessagesFromUnfilteredChannels={true}
          channelRenderFilterFn={(channels) =>
              customChannelFilterFunction(channels, "public_with_membership",userGroupIds) // Event type
            }          Preview={Preview} 
          Paginator={Paginator}
          onClick={onClick} setActiveChannel={setActiveChannel} languageCode={languageCode} 
        />
      </div>
    </div>
  );
};

export default MessagingSidebar;
